/**
 * API response shapes from spade-outreach-backend. These mirror the Drizzle
 * row shapes — keep them in sync manually for v1 (no shared types package yet).
 */

export interface OutreachTeamSettings {
  id: number;
  /** Team code — loosely typed because teams are CRUDable via /admin/teams. */
  team: string;
  screenshotRequired: boolean;
  multipleNichesAllowed: boolean;
  /**
   * When true, the morning verification cron resolves this team's invites
   * against the clipping endpoint (PENDING → VERIFIED / CONVERTED on real
   * signal). When false (default), the cron skips them and AdminInvites
   * renders a muted "Not tracked" pill on PENDING rows so admins can tell
   * the difference between "clipping hasn't found them yet" and "this team
   * doesn't use clipping at all."
   */
  clippingResolveRequired: boolean;
  /** Days before a non-converted invite unlocks for re-outreach. null = disabled. */
  reOutreachCooldownDays: number | null;
  /**
   * When true, outreachers on this team can soft-delete their own invites
   * (SPA-618). Delete is non-destructive — the row is hidden from default
   * list surfaces but stays queryable via admin. Admins bypass this gate.
   */
  canDeleteInvites: boolean;
  /**
   * Team default for bulk import (CSV / Excel). Outreachers can be switched
   * on or off individually on top of this; `bulkImportEnabled` on /portal/me
   * is the effective answer.
   */
  bulkImportEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Lightweight view of the inviter's team that the portal needs at render time
 * — full mutation surface lives in the admin hooks.
 */
export interface MeTeam {
  code: string;
  displayName: string;
  allowedChannels: InviteChannel[];
  allowedCategories: string[];
}

export type Me =
  | {
      role: 'admin';
      clerkUserId: string;
      orgId: string | null;
      name: string;
      email: string | null;
      /** Always false for admins (bulk import is an outreacher tool). */
      bulkImportEnabled?: boolean;
    }
  | {
      role: 'outreacher';
      inviter: OutreachInviter;
      /** Null only in the edge case where the inviter's team row was deleted out from under them. */
      team: MeTeam | null;
      teamSettings: OutreachTeamSettings;
      /**
       * Whether this outreacher may use Bulk import: their own override if
       * set, else their team's rule. Optional until every backend sends it.
       */
      bulkImportEnabled?: boolean;
    };

export type RequiredInviterField =
  | 'clippingId'
  | 'paypalEmail'
  | 'email'
  | 'inviterPublicId'
  | 'name';

export interface RecipientValidationIssue {
  field: RequiredInviterField;
  reason: 'MISSING' | 'INVALID';
  message: string;
}

export interface PayrollLineItem {
  inviterId: number;
  publicId: string;
  name: string;
  /** Team code — loose (teams are CRUDable). */
  team: string;
  email: string | null;
  paypalEmail: string | null;
  compensationModel: CompensationModel;
  retainerAmount: string | null;
  owed: string;
  adjustmentsTotal: string;
  retainerDue: string;
  totalDue: string;
  /** Sum of PAID payouts in the current period window (weekly or monthly). */
  paidThisPeriod: string;
  /** Sum of PENDING+SUBMITTED payouts for this inviter. > 0 blocks new Pay. */
  inFlight: string;
  /** publicId of the most recent open payout (PENDING/SUBMITTED) for deep-link. */
  openPayoutPublicId: string | null;
  lastPayoutAt: string | null;
  lastPayoutStatus: string | null;
  /**
   * Pre-payroll lint: which provider this inviter will route to and which
   * required fields are missing. When `missingFields.length > 0`, the Pay
   * button is disabled and the row carries a destructive pill explaining
   * what to fix on the inviter row.
   */
  lint: {
    providerKey: string;
    missingFields: RecipientValidationIssue[];
  };
}

export type PayrollPeriod = 'weekly' | 'monthly';

export interface PayrollResponse {
  period: PayrollPeriod;
  periodOf: string;
  periodStart: string;
  periodEnd: string;
  inviters: PayrollLineItem[];
}

/**
 * Kinds of `lockedValue` breakdown component the backend can emit. Kept in
 * sync with `LOCKED_VALUE_COMPONENT_KINDS` in spade-outreach-backend's
 * services/payoutCalculator.ts. The frontend treats unknown kinds by
 * falling back to the server-provided `label`.
 */
export type LockedValueComponentKind =
  | 'BASE_LOG'
  | 'NICHE_LOG'
  | 'BASE_JOIN'
  | 'NICHE_JOIN'
  | 'BASE_POST'
  | 'NICHE_POST'
  | 'BRACKET_BONUS'
  | 'FLOOR_AGAINST_PAID';

export interface LockedValueComponent {
  kind: LockedValueComponentKind;
  amount: string;
  label: string;
  meta?: Record<string, string | number | boolean>;
}

export interface PayrollBreakdownInvite {
  inviteId: number;
  publicId: string;
  targetIdentifier: string;
  channel: InviteChannel;
  niches: string[];
  category: string;
  status: InviteStatus;
  lockedValue: string;
  amountPaid: string;
  adjustmentsTotal: string;
  /** lockedValue - amountPaid + adjustmentsTotal, clamped at 0. */
  contribution: string;
  followerCount: number | null;
  hasPosted: boolean;
  isInServer: boolean | null;
  createdAt: string;
  convertedAt: string | null;
  screenshotKey: string | null;
  /**
   * Per-component snapshot of what makes up `lockedValue`. NULL for invites
   * written before backend migration 0032 that haven't been re-snapshotted or
   * backfilled yet.
   */
  lockedValueComponents: LockedValueComponent[] | null;
}

export interface PayrollBreakdownRecentPayout {
  publicId: string;
  status: PayoutStatus;
  totalAmount: string;
  providerKey: PayoutProviderKey;
  paidAt: string | null;
  payrollWeekOf: string;
}

export interface PayrollBreakdown {
  inviter: {
    id: number;
    publicId: string;
    name: string;
    team: string;
    compensationModel: CompensationModel;
    retainerAmount: string | null;
  };
  period: PayrollPeriod;
  periodOf: string;
  totalDue: string;
  retainerDue: string;
  paidThisPeriod: string;
  byStatus: Array<{
    status: InviteStatus;
    count: number;
    contribution: string;
    invites: PayrollBreakdownInvite[];
  }>;
  recentPayouts: PayrollBreakdownRecentPayout[];
}

export interface ObligationAdjustment {
  id: number;
  inviteId: number;
  amount: string;
  reason: string;
  adjustedByClerkUserId: string;
  createdAt: string;
}

export type InviteChannel = 'DISCORD' | 'TIKTOK' | 'INSTAGRAM' | 'YOUTUBE' | 'TWITTER';

/**
 * Canonical channel list — the same set spade-outreach-backend's
 * `INVITE_CHANNELS` enum accepts. Use this anywhere the UI iterates over
 * channels (team-edit picker, filter dropdowns, prospect lookup). The
 * ordering is the operator-friendly default: most-used first, Discord last
 * since it's the only off-platform target.
 */
export const INVITE_CHANNELS = [
  'TIKTOK',
  'INSTAGRAM',
  'YOUTUBE',
  'TWITTER',
  'DISCORD',
] as const satisfies ReadonlyArray<InviteChannel>;
// NOT_FOUND retired backend-side in migration 0024 — historical rows were
// migrated to PENDING. Kept out of this union so the filter UI can't send it.
export type InviteStatus = 'PENDING' | 'VERIFIED' | 'CONVERTED' | 'REJECTED';
/** Loose alias — categories are CRUDable via /admin/categories. */
export type InviteCategory = string;
export type CompensationModel = 'PER_CONVERSION' | 'RETAINER';

export interface OutreachInviter {
  id: number;
  publicId: string;
  clerkUserId: string | null;
  clippingId: string | null;
  email: string | null;
  name: string;
  /** Code of any row in outreach_teams (admins add teams at runtime). */
  team: string;
  compensationModel: CompensationModel;
  retainerAmount: string | null;
  paypalEmail: string | null;
  isActive: boolean;
  notes: string | null;
  /** Bulk import override: true / false, or null to follow the team rule. */
  bulkImportEnabled?: boolean | null;
  /** Creators to message per ISO week; null when no quota is set. */
  weeklyMessageQuota?: number | null;
  /**
   * How to reach them outside the portal. They fill these in themselves
   * (My details); admins can edit them. Optional until every backend sends them.
   */
  contactWhatsapp?: string | null;
  contactDiscord?: string | null;
  contactTelegram?: string | null;
  contactOther?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Message quotas (GET /admin/inviters/message-quotas) ──────────────────
export type QuotaStatus = 'none' | 'under' | 'hit' | 'over';

export interface QuotaWeek {
  /** Monday of the ISO week (UTC), YYYY-MM-DD. */
  weekStart: string;
  messages: number;
  status: QuotaStatus;
}

export interface QuotaRow {
  inviterId: number;
  publicId: string;
  name: string;
  team: string;
  compensationModel: CompensationModel;
  weeklyMessageQuota: number | null;
  /** One entry per `MessageQuotaReport.weeks`, oldest first. */
  weeks: QuotaWeek[];
}

/** GET /portal/contacts/weekly-minimum: the caller's own week. */
export interface MyWeeklyMinimum {
  /** Monday of the week in progress (UTC), YYYY-MM-DD. */
  weekStart: string;
  /** Their weekly message quota, shown as a minimum. Null when none is set. */
  minimum: number | null;
  messages: number;
}

export interface MessageQuotaReport {
  weeks: string[];
  /** The week in progress; the last entry of `weeks`. */
  currentWeek: string;
  rows: QuotaRow[];
}

export interface OutreachInvite {
  id: number;
  publicId: string;
  inviterId: number;
  channel: InviteChannel;
  targetIdentifier: string;
  category: InviteCategory;
  niches: string[];
  email: string | null;
  notes: string | null;
  screenshotKey: string | null;
  status: InviteStatus;
  matchedUserId: string | null;
  externalSocialAccountId: string | null;
  isInServer: boolean | null;
  hasPosted: boolean | null;
  postCount: number;
  followerCount: number | null;
  /** Best-known followers: the invite's own count, else what discovery measured. Absent on an older backend. */
  estFollowers?: number | null;
  /** Average views per post as discovery measured it. Absent on an older backend. */
  avgViews?: number | null;
  lastResolvedAt: string | null;
  convertedAt: string | null;
  lockedValue: string | null;
  bracketBonusAttributed: string;
  amountPaid: string;
  outreachPayoutId: number | null;
  discoveryFolderName: string | null;
  /**
   * Set when the invite passed its team's re-log window without the creator
   * joining: the slot is freed and anyone can log the creator again. The
   * status stays PENDING in the database; the UI shows it as Expired.
   */
  releasedAt?: string | null;
  /** Screenshot audit verdict — null until someone reviews it. */
  screenshotVerdict?: ScreenshotVerdict | null;
  screenshotReviewedAt?: string | null;
  screenshotReviewNote?: string | null;
  /**
   * Soft-delete marker (SPA-618). When non-null the row is treated as
   * "deleted" — surfaces show a Deleted pill and hide row actions like
   * Reject / Adjust, but the record itself is preserved.
   */
  deletedAt: string | null;
  deletedByInviterId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvitePost {
  postId: string;
  matchedUserId: string;
  postUrl: string | null;
  platform: string;
  thumbnailUrl: string | null;
  campaignId: string | null;
  campaignName: string | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  engagementRate: number | null;
  postedAt: string | null;
  lastSyncedAt: string | null;
  /**
   * Attribution flags stamped by the backend using the SAME window math as the
   * conversion writer. `inWindow=true` = this post counted toward the invite's
   * postCount / hasPosted. `filteredReason` narrates why an out-of-window post
   * didn't count (surfaced as a tooltip in the admin table).
   *
   * Optional because the batch-posts endpoint doesn't populate them yet (it
   * fans out across many invites, no shared window). Only `/admin/invites/:id/posts`
   * sets them today.
   */
  inWindow?: boolean;
  filteredReason?: PostFilterReason;
}

export type PostFilterReason =
  | null
  | 'no_postedAt'
  | 'unparseable_postedAt'
  | 'before_window';

export type AttributionReason =
  | null
  | 'no_matched_user'
  | 'clipping_unreachable'
  | 'clips_exist_all_out_of_window'
  | 'no_approved_clips';

export interface AttributionDiagnostic {
  bufferHours: number;
  windowStart: string;
  postsFetched: boolean;
  allTimeApprovedClipCount: number | null;
  windowedPostCount: number;
  inWindowEarliestPostedAt: string | null;
  perPost: Array<{
    matchedUserId: string;
    postedAt: string | null;
    views: number | null;
    campaignName?: string | null;
    inWindow: boolean;
    filteredReason: PostFilterReason;
  }>;
  reason: AttributionReason;
}

export interface OutreachRate {
  id: number;
  category: InviteCategory;
  rateType: 'LOG_BONUS' | 'JOIN_BONUS' | 'POST_BASE' | 'NICHE_BONUS' | 'INFLUENCER_FLAT';
  niche: string | null;
  amount: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface OutreachFollowerBracket {
  id: number;
  category: InviteCategory;
  minFollowers: number;
  rate: string;
  isActive: boolean;
}

export interface OutreachNiche {
  id: number;
  code: string;
  displayName: string;
  isFocused: boolean;
  displayOrder: number;
  /**
   * Categories this niche is restricted to. Empty array means the niche is
   * global (shows for every category in the outreacher's category picker).
   */
  categoryCodes: string[];
  /**
   * Hidden niches are left out of every picker (the portal /niches endpoint
   * drops them unless asked) but still name old invites, rates and reports.
   * Admin /niches always includes them with this flag set.
   */
  isHidden?: boolean;
  hiddenAt?: string | null;
}

/** Everything that references a niche code — GET /admin/niches?withUsage=true. */
export interface NicheUsage {
  /** Valid invites (not deleted, not rejected). */
  invitesLive: number;
  /** Every invite row, including rejected and hidden ones. */
  invitesTotal: number;
  lastUsedAt: string | null;
  prospects: number;
  creatorRequests: number;
  challenges: number;
  ratesActive: number;
  ratesInactive: number;
  discoverySuggestions: number;
}

export interface OutreachNicheWithUsage extends OutreachNiche {
  usage: NicheUsage;
}

export type PayoutProviderKey = 'financial' | 'clipping' | 'local';
export type PayoutStatus = 'PENDING' | 'SUBMITTED' | 'PAID' | 'FAILED' | 'CANCELLED';

export interface PayoutLineItem {
  inviteId: number;
  targetIdentifier: string;
  amount: string;
  /** Amount written off via SETTLEMENT when the payout was below owed. */
  writeOffAmount?: string;
}

export interface OutreachPayout {
  id: number;
  publicId: string;
  inviterId: number;
  payrollWeekOf: string;
  lineItems: PayoutLineItem[];
  additionalAmount: string;
  totalAmount: string;
  settledAmount: string;
  status: PayoutStatus;
  providerKey: PayoutProviderKey;
  providerExternalId: string | null;
  submittedAt: string | null;
  paidAt: string | null;
  paidVia: string | null;
  externalReceiptId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /admin/payouts/:publicId — line items enriched with the invite publicId. */
export interface OutreachPayoutDetail extends Omit<OutreachPayout, 'lineItems'> {
  lineItems: Array<PayoutLineItem & { invitePublicId: string | null }>;
}

export type CreatorRequestType = 'CREATOR' | 'NICHE';

export type CreatorRequestStatus =
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'AWAITING_RESPONSE'
  | 'AWAITING_RATE'
  | 'ROSTERED'
  | 'CANCELLED'
  | 'COMPLETED';

export type CreatorRequestCancellationReason =
  | 'GHOSTED'
  | 'TOO_EXPENSIVE'
  | 'REFUSING_PROMOS'
  | 'OUT_OF_NICHE'
  | 'OTHER';

export interface CreatorRequest {
  id: number;
  publicId: string;
  requestType: CreatorRequestType;
  targetTeam: string;
  channel: InviteChannel | null;
  targetIdentifier: string | null;
  /** Niche codes the request targets. Empty for CREATOR-type requests. */
  niches: string[];
  categoryHint: string | null;
  minFollowers: number | null;
  brief: string;
  requesterName: string;
  requesterEmail: string;
  requesterExternalId: string;
  status: CreatorRequestStatus;
  cancellationReason: CreatorRequestCancellationReason | null;
  cancellationNote: string | null;
  assignedInviterId: number | null;
  rosterInviteId: number | null;
  createdAt: string;
  updatedAt: string;
}

export type CreatorRequestCommentAuthorRole = 'admin' | 'outreacher' | 'cm';

export interface CreatorRequestComment {
  id: number;
  requestId: number;
  authorSide: 'FINANCIAL' | 'OUTREACH';
  authorRole: CreatorRequestCommentAuthorRole | null;
  authorName: string;
  body: string;
  createdAt: string;
}

export interface CreatorRequestDetail extends CreatorRequest {
  comments: CreatorRequestComment[];
  attachedInvites?: CreatorRequestInviteAttachment[];
}

export interface CreatorRequestInviteAttachment {
  attachmentId: number;
  inviteId: number;
  invitePublicId: string;
  channel: InviteChannel;
  targetIdentifier: string;
  niches: string[];
  inviteStatus: string;
  isRoster: boolean;
  attachedAt: string;
  attachedByInviterId: number | null;
}

export interface CreatorRequestRequester {
  requesterExternalId: string;
  requesterName: string;
  requesterEmail: string;
  requestCount: number;
}

export type CreatorRequestBatchStatus = 'OPEN' | 'COMPLETED' | 'CANCELLED';

/** Batch list row — includes progress rollup for the queue tile. */
export interface CreatorRequestBatch {
  id: number;
  publicId: string;
  name: string;
  targetTeam: string;
  channel: InviteChannel | null;
  brief: string;
  requesterName: string;
  requesterEmail: string;
  requesterExternalId: string;
  status: CreatorRequestBatchStatus;
  cancellationNote: string | null;
  itemCount: number;
  checkedCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Batch detail: parent + full child list + comments thread. */
export interface CreatorRequestBatchDetail extends CreatorRequestBatch {
  items: CreatorRequest[];
  comments: CreatorRequestComment[];
}

export type AccessRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface OutreachAccessRequest {
  id: number;
  clerkUserId: string;
  email: string;
  name: string;
  requestedTeam: string | null;
  note: string;
  status: AccessRequestStatus;
  reviewedAt: string | null;
  reviewedBy: string | null;
  rejectionReason: string | null;
  createdInviterId: number | null;
  createdAt: string;
  updatedAt: string;
}

export type DiscoveryProspectStatus =
  | 'AVAILABLE'
  | 'CLAIMED'
  /** At least one outreacher has DM'd them. Non-exclusive: the lead stays in
   *  everyone's queue and anyone can still log the invite. */
  | 'CONTACTED'
  | 'DISMISSED'
  | 'PROMOTED';

/**
 * Screenshot audit outcome. UNCLEAR is kept distinct from REJECTED so
 * "couldn't tell" never gets counted as "they cheated".
 */
export type ScreenshotVerdict = 'APPROVED' | 'REJECTED' | 'UNCLEAR';

export interface ScreenshotAuditItem {
  invitePublicId: string;
  targetIdentifier: string;
  channel: InviteChannel;
  category: string;
  status: string;
  inviterName: string;
  inviterTeam: string;
  createdAt: string;
  lockedValue: string | null;
  /** Presigned S3 URL; null when signing failed for this one object. */
  screenshotUrl: string | null;
  /** Outreachers who DM'd this creator. >1 means the invite won a race. */
  contactCount: number;
  verdict: ScreenshotVerdict | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

export interface ScreenshotAuditQueue {
  items: ScreenshotAuditItem[];
  pendingTotal: number;
  counts: { approved: number; rejected: number; unclear: number };
}

/**
 * A creator someone DM'd where nobody has logged a reply yet.
 *
 * Returned by the invites endpoints under `status=CONTACTED`. There is no
 * invite behind these — that's what "not replied" means — so they carry none
 * of an invite's money or verification fields.
 */
export interface ContactedNotReplied {
  publicId: string;
  channel: InviteChannel;
  targetIdentifier: string;
  firstContactedAt: string;
  lastContactedAt: string;
  attemptCount: number;
  outreacherCount: number;
  contactedBy: { inviterId: number; inviterName: string; inviterTeam: string }[];
  note: string | null;
}

/** One outreacher's record of having messaged a creator. */
export interface ContactAttempt {
  publicId: string;
  inviterId: number;
  inviterName: string;
  inviterTeam: string;
  firstContactedAt: string;
  lastContactedAt: string;
  attemptCount: number;
  note: string | null;
  /** Set on the attempt that became an invite — i.e. who got the reply. */
  resultingInvitePublicId: string | null;
}

/** Response of GET /portal/outreach-history — everything known about one handle. */
export interface OutreachHistory {
  channel: InviteChannel;
  targetIdentifier: string;
  attempts: ContactAttempt[];
  invite: {
    publicId: string;
    target: string;
    status: string;
    convertedAt: string | null;
  } | null;
  prospect: {
    publicId: string;
    target: string;
    status: DiscoveryProspectStatus;
    claimedByName: string | null;
    createdAt: string;
  } | null;
}

/**
 * Lead pushed in from spade-discovery, sitting in the claim queue. Outreachers
 * see these on the Discovery Queue page; once claimed and DMed, they're
 * promoted into a full OutreachInvite via /portal/prospects/:id/promote.
 */
export interface DiscoveryProspect {
  id: number;
  publicId: string;
  tenantId: string;
  discoveryLeadId: string;
  sourceTeamId: string | null;
  sourceFolderPublicId: string | null;
  sourceFolderName: string | null;
  channel: InviteChannel;
  targetIdentifier: string;
  enrichmentSnapshot: Record<string, unknown>;
  suggestedCategory: string | null;
  niches: string[];
  notes: string | null;
  assignedToInviterId: number | null;
  assignedToTeam: string | null;
  status: DiscoveryProspectStatus;
  claimedByInviterId: number | null;
  claimedByInviterName: string | null;
  claimedAt: string | null;
  dismissedAt: string | null;
  dismissReason: string | null;
  promotedToInviteId: number | null;
  promotedAt: string | null;
  /** Everyone who has DM'd this prospect, in first-contacted order. */
  contactedBy: { inviterId: number; inviterName: string }[];
  /** Whether the calling outreacher is one of them. Null on admin surfaces. */
  contactedByMe: boolean | null;
  /**
   * Whether the CALLING outreacher marked this lead irrelevant to its folder,
   * and the note they left. One person's mark is invisible to teammates by
   * design — only an admin removes a lead for everyone.
   */
  flaggedByMe: boolean | null;
  flagNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DiscoveryFolderSummary {
  sourceFolderPublicId: string | null;
  sourceFolderName: string | null;
  /** Workable leads — AVAILABLE plus CONTACTED (contacted is not taken). */
  availableCount: number;
  /** Subset of the above that someone has already messaged. */
  contactedCount: number;
  totalCount: number;
  lastExportedAt: string;
  /**
   * Admin briefing for this folder — which campaigns to push these creators
   * toward, and how to pitch them. Read live from outreach_folder_guidance
   * rather than denormalised, so edits take effect immediately.
   */
  recommendedCampaigns: string | null;
  /** Clipping campaigns this folder targets, resolved live (never stored). */
  campaigns: FolderCampaign[];
  pitchNotes: string | null;
}

// ── Gamification ────────────────────────────────────────────────────────

export type ChallengeMetric =
  | 'MOST_INVITES'
  | 'MOST_CONVERSIONS'
  | 'HIGHEST_CONVERSION_RATE'
  | 'MOST_POSTS'
  | 'MOST_VIEWS';

export const CHALLENGE_METRICS: readonly ChallengeMetric[] = [
  'MOST_INVITES',
  'MOST_CONVERSIONS',
  'HIGHEST_CONVERSION_RATE',
  'MOST_POSTS',
  'MOST_VIEWS',
] as const;

export const CHALLENGE_METRIC_LABELS: Record<ChallengeMetric, string> = {
  MOST_INVITES: 'Most Invites',
  MOST_CONVERSIONS: 'Most Conversions',
  HIGHEST_CONVERSION_RATE: 'Highest Conversion Rate',
  MOST_POSTS: 'Most Posts',
  MOST_VIEWS: 'Most Views',
};

export interface OutreachChallenge {
  id: number;
  publicId: string;
  name: string;
  description: string | null;
  metric: ChallengeMetric;
  targetTeam: string | null;
  targetNiche: string | null;
  startDate: string;
  endDate: string;
  isActive: boolean;
  prizeAmount: string | null;
  prizeDescription: string | null;
  prizeAwardedAt: string | null;
  prizeAwardedToInviterId: number | null;
  /**
   * Set once the challenge has ended: the final standings, frozen, and the
   * proposed winner waiting for an admin to approve or reject the prize.
   * Nothing is paid until then. Optional: older backends don't send it.
   */
  prizePending?: ChallengePrizePending | null;
  /** The admin's call on the pending prize, once made. */
  prizeDecision?: ChallengePrizeDecision | null;
  createdByClerkUserId: string;
  createdAt: string;
  updatedAt: string;
}

/** One row of a challenge's frozen final standings, best first. */
export interface ChallengeSnapshotStanding {
  inviterId: number;
  name: string;
  value: number;
}

export interface ChallengePrizePending {
  /** Proposed winner (first place). Null when nobody scored. */
  inviterId: number | null;
  inviterName: string | null;
  /** When the standings were frozen. */
  at: string;
  standings: ChallengeSnapshotStanding[];
  /** First place is shared: approving needs the winner picked. */
  tie: boolean;
}

export interface ChallengePrizeDecision {
  decision: 'approved' | 'rejected';
  /** Clerk user id (or name) of the admin who decided. */
  decidedBy: string | null;
  decidedAt: string;
  /** Required on reject; optional otherwise. */
  reason: string | null;
}

export interface LeaderboardEntry {
  inviterId: number;
  inviterName: string;
  team: string;
  value: number;
}

export interface LeaderboardResponse {
  byInvites: LeaderboardEntry[];
  byConversions: LeaderboardEntry[];
}

export interface ChallengeStanding {
  inviterId: number;
  inviterName: string;
  team: string;
  value: number;
  rank: number;
}

export interface ChallengeWithStandings {
  challenge: OutreachChallenge;
  standings: ChallengeStanding[];
}

export interface StreakInfo {
  inviterId: number;
  inviterName: string;
  team: string;
  currentStreak: number;
}

export interface StreaksResponse {
  myStreak: number;
  topStreaks: StreakInfo[];
}

// ── Cron ────────────────────────────────────────────────────────────────

export type CronRunStatus = 'OK' | 'FAILED';
export type CronRunTrigger = 'SCHEDULE' | 'MANUAL';

/** One execution of a registered cron job, recorded by cronRunRecorder. */
export interface CronRun {
  id: number;
  jobName: string;
  trigger: CronRunTrigger;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  status: CronRunStatus;
  summary: Record<string, unknown> | null;
  errorMessage: string | null;
  createdAt: string;
}

/** GET /admin/cron-runs response — one bucket per known job, runs newest first. */
export interface CronRunsResponse {
  perJob: number;
  groups: {
    jobName: string;
    /** In flight right now (runs are recorded when they finish). Absent from older backends. */
    running?: boolean;
    runningSince?: string | null;
    runs: CronRun[];
  }[];
}

/** 202 payload for POST /admin/cron-runs/:jobName/trigger. */
export interface CronRunTriggerAcceptance {
  jobName: string;
  startedAt: string;
}

// ---- Admin analytics (GET /admin/analytics/*) ------------------------------

export interface TrendPoint {
  week: string;
  invites: number;
  conversions: number;
}

export interface InviterAnalyticsDetail {
  statusBreakdown: { status: string; count: number; locked: string }[];
  nicheMix: { niche: string; count: number; conversions: number }[];
  channelMix: { channel: string; count: number; conversions: number }[];
  trend: TrendPoint[];
}

/**
 * An example search shown to outreachers. Suggestions, never restrictions —
 * the response carries `isSuggestion` so no surface can render it as policy.
 */
export interface SearchIdea {
  publicId: string;
  term: string;
  searchMode: 'hashtag' | 'sound';
  musicId: string | null;
  reach: number | null;
  band: string | null;
  source: 'focus_niche' | 'campaign';
  sourceLabel: string;
  approvedAt: string | null;
}

export interface SearchIdeasResponse {
  isSuggestion: boolean;
  configured: boolean;
  ideas: SearchIdea[];
}

/** A clipping campaign a folder is targeting, resolved live. */
export interface FolderCampaign {
  id: string;
  name: string;
  status: string;
  description: string | null;
  /** Null when unpublished — the copy-link button is hidden. */
  clipperUrl: string | null;
  niches: string[];
}

// ── home & proofs (ux2) ──

/**
 * One team's pipeline for a period, from GET /admin/analytics/team-pipeline.
 *
 * - messages: "I messaged them" records first made in the period.
 * - invites: invites logged in the period.
 * - joined: of those invites, how many have joined or posted (a cohort).
 * - posted: first approved posts in the period (by conversion date).
 * - joinedInPeriod: joins recorded in the period. 0 for history (the column
 *   is new), so it isn't shown yet.
 */
export interface TeamPipelineRow {
  team: string;
  teamDisplayName?: string;
  messages: number;
  invites: number;
  joined: number;
  posted: number;
  joinedInPeriod: number;
}

// ── Bulk import (/portal/bulk/*) ─────────────────────────────────────────

/** One creator to check or act on. `targetIdentifier` is the bare username. */
export interface BulkItem {
  channel: InviteChannel;
  targetIdentifier: string;
}

export type BulkCheckStatus = 'new' | 'messaged_by_me' | 'mine' | 'taken' | 'invalid';

export interface BulkCheckResult {
  /** Position in the request's `items`. */
  index: number;
  channel: InviteChannel;
  username: string | null;
  status: BulkCheckStatus;
  inviteStage?: string | null;
  heldByName?: string | null;
  reason?: string | null;
}

export interface BulkCheckResponse {
  results: BulkCheckResult[];
}

export interface BulkMessagedResponse {
  results: Array<{ index: number; ok: boolean; username?: string; code?: string; message?: string }>;
  recorded: number;
}

export interface BulkLogItem extends BulkItem {
  /** Creator type code. Optional when the team has a single category. */
  category?: string;
  niches?: string[];
  email?: string;
  notes?: string;
  /** Multipart field name carrying this row's screenshot. */
  screenshotField?: string;
}

export interface BulkLogResponse {
  results: Array<{ index: number; ok: boolean; invitePublicId?: string; code?: string; message?: string }>;
  created: number;
}

/**
 * SPA-1208 + transparency. Response shape for
 * GET /portal/invites/:publicId/breakdown (portal-safe variant) and
 * GET /admin/invites/:id/owed-breakdown (admin fidelity — includes
 * createdBy and source refs).
 */
export type BreakdownKind =
  | 'PAYOUT'
  | 'RECONCILE'
  | 'ADJUSTMENT'
  | 'REJECT'
  | 'SETTLEMENT'
  | 'IMPORT'
  | 'REVERSAL';

export interface BreakdownComponent {
  kind: BreakdownKind;
  label: string;
  signedAmount: string;
  occurredAt: string; // ISO
  reason: string;
  eventPublicId: string;
  reversesEventPublicId: string | null;
  payoutPublicId: string | null;
  siblingInvitePublicId: string | null;
  /** Null in the portal shape. */
  createdBy?: string | null;
  sourceRefKind?: string | null;
  sourceRefId?: string | null;
}

export interface InviteOwedBreakdownResponse {
  lockedValue: string;
  amountPaid: string;
  adjustmentsTotal: string;
  netOwed: string;
  components: BreakdownComponent[];
  explanation: string;
}

// ── Outreach accounts vault (admin only) ──────────────────────────────────
// The social logins outreachers DM from. Outreachers never see these, not
// even their own. List responses never carry the password: `hasPassword`
// says one is stored, and POST /admin/outreach-accounts/:id/reveal returns
// it (audited).

export type OutreachAccountStatus = 'ACTIVE' | 'BANNED' | 'RETIRED';
export const OUTREACH_ACCOUNT_STATUSES: OutreachAccountStatus[] = ['ACTIVE', 'BANNED', 'RETIRED'];

export interface OutreachAccount {
  publicId: string;
  platform: InviteChannel;
  username: string;
  email: string | null;
  notes: string | null;
  status: OutreachAccountStatus;
  hasPassword: boolean;
  inviter: { publicId: string; name: string; team: string; isActive: boolean };
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  lastRevealedAt: string | null;
}

export interface OutreachAccountCounts {
  inviterPublicId: string;
  inviterName: string;
  inviterTeam: string;
  inviterIsActive: boolean;
  total: number;
  active: number;
  byPlatform: Partial<Record<InviteChannel, number>>;
}

export interface OutreachAccountsList {
  accounts: OutreachAccount[];
  countsByInviter: OutreachAccountCounts[];
}

/** Body for create / edit. `inviterId` is a public id or numeric id. */
export interface OutreachAccountInput {
  inviterId?: string;
  platform?: InviteChannel;
  username?: string;
  /** A string sets it, null clears it, absent leaves it alone. */
  password?: string | null;
  email?: string | null;
  notes?: string | null;
  status?: OutreachAccountStatus;
}

export type OutreachAccountBulkResult =
  | { index: number; status: 'created'; publicId: string; platform: string; username: string }
  | { index: number; status: 'duplicate'; platform: string; username: string; message: string }
  | { index: number; status: 'invalid'; message: string };

export interface OutreachAccountBulkResponse {
  results: OutreachAccountBulkResult[];
  created: number;
  duplicates: number;
  invalid: number;
}

export type OutreachAccountAuditAction = 'REVEAL' | 'COPY' | 'PASSWORD_SET' | 'PASSWORD_CLEARED' | 'DELETE';

export interface OutreachAccountAuditEvent {
  id: number;
  action: OutreachAccountAuditAction;
  actorUserId: string;
  actorName: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}
